import { useState } from "react";

import { ApiService } from "common/services/ApiService";
import { useAuth } from "../../../context/AuthContext";

import { Backdrop, CircularProgress } from "@mui/material";
import Dialog from "@mui/material/Dialog";

import { snakeCaseObject } from "helpers/TextHelpers";
import { useLocation } from "react-router-dom";
import GenericAlert from "../GenericAlert/GenericAlert";
import EstablishmentSelectionModal from "./components/EstablishmentSelectionModal";
import LogInForm from "./components/LogInForm";
import SignUpForm from "./components/SignUpForm";

const DEFAULT_VALUE_FORM_MAP = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    repeatedPassword: "",
    birthday: "",
    province: "",
    city: "",
    establishments: [],
    isTeacher: false,
    isAdmin: false,
    isStudent: true,
    rolesPending: [],
};

export default function AuthFormDialog({
    type,
    open,
    setOpen,
    handleChangeForms,
    checkPendingNotification,
    pendingNotificationIntervalId,
    setPendingNotificationIntervalId,
    loggedByGoogle = false,
    getConfigurationAuthMe,
    setConfigurationGoogle,
    setRoleSelectedCallback,
    afterCompleteRegisterGoogle,
    dataGoogle,
}) {
    const { auth, setAuth } = useAuth();
    const location = useLocation();

    const [logInFormData, setLogInFormData] = useState({
        email: "",
        password: "",
    });

    const [openEstablishmentModal, setOpenEstablishmentModal] = useState(false);

    const [openLoader, setOpenLoader] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [alertSeverity, setAlertSeverity] = useState("");
    const [alertMsg, setAlertMsg] = useState("");
    const [showAlert, setShowAlert] = useState(false);

    const [signUpFormData, setSignUpFormData] = useState(DEFAULT_VALUE_FORM_MAP);

    const handleClose = () => {
        setOpen(false);
    };

    const cleanUrl = () => {
        const currentUrl = new URL(window.location.origin + location.pathname + location.search);

        const allowedParams = ["access_token", "refresh_token"];
        for (let param of Array.from(currentUrl.searchParams.keys())) {
            if (!allowedParams.includes(param)) {
                currentUrl.searchParams.delete(param);
            }
        }

        window.history.replaceState(null, "", currentUrl.toString());
    };

    const handleLogIn = async (loginData, origin = "login") => {
        try {
            const data = loginData || logInFormData;
            const res = await ApiService.login(data);
            if (res.status === 200) {
                handleClose();
                localStorage.setItem("access", res.data.access);
                const authMeData = await getConfigurationAuthMe(res.data.access);
                if (origin === "login" && authMeData && !authMeData.data.roles.includes(4)) {
                    setOpenEstablishmentModal(true);
                }
                setLogInFormData({
                    email: "",
                    password: "",
                });
            } else if (res.status === 401) {
                setErrorMessage("Email o contraseña incorrectos");
                setShowError(true);
                setTimeout(() => {
                    setShowError(false);
                    setErrorMessage("");
                }, 3000);
            } else {
                setErrorMessage("Ocurrió un error, intente nuevamente!");
                setShowError(true);
                setTimeout(() => {
                    setShowError(false);
                    setErrorMessage("");
                }, 3000);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                setErrorMessage("Email o contraseña incorrectos");
                setShowError(true);
                setTimeout(() => {
                    setShowError(false);
                    setErrorMessage("");
                }, 3000);
            } else {
                setErrorMessage("Ocurrió un error, intente nuevamente!");
                setShowError(true);
                setTimeout(() => {
                    setShowError(false);
                    setErrorMessage("");
                }, 3000);
            }
        }
        setOpenLoader(false);
    };
    const handleShowAlert = (variant, msg) => {
        setAlertSeverity(variant);
        setAlertMsg(msg);
        setShowAlert(true);
        setTimeout(() => {
            setShowAlert(false);
            setAlertSeverity("");
            setAlertMsg("msg");
        }, 5000);
    };
    const handleSignUp = async (evt) => {
        evt.preventDefault();
        if (
            (!loggedByGoogle && !signUpFormData.firstName) ||
            (!loggedByGoogle && !signUpFormData.lastName) ||
            (!loggedByGoogle && !signUpFormData.email) ||
            (!loggedByGoogle && (!signUpFormData.password || !signUpFormData.repeatedPassword)) ||
            !signUpFormData.birthday ||
            !signUpFormData.province ||
            !signUpFormData.city
        ) {
            setErrorMessage("Debe completar todos los campos");
            setShowError(true);
            setTimeout(() => {
                setShowError(false);
                setErrorMessage("");
            }, 3000);
            return;
        }
        if (!loggedByGoogle && signUpFormData.password !== signUpFormData.repeatedPassword) {
            setErrorMessage("Las contraseñas no coinciden");
            setShowError(true);
            setTimeout(() => {
                setShowError(false);
                setErrorMessage("");
            }, 3000);
            return;
        }
        if (!loggedByGoogle && signUpFormData.password.length < 8) {
            setErrorMessage("La contraseña debe tener un mínimo de 8 caracteres");
            setShowError(true);
            setTimeout(() => {
                setShowError(false);
                setErrorMessage("");
            }, 3000);
            return;
        }

        setOpenLoader(true);
        signUpFormData.rolesPending = [];
        signUpFormData.isTeacher && signUpFormData.rolesPending.push("teacher");
        signUpFormData.isAdmin && signUpFormData.rolesPending.push("admin");
        signUpFormData.isStudent && signUpFormData.rolesPending.push("student");

        const data = snakeCaseObject(signUpFormData);

        try {
            const res = !loggedByGoogle
                ? await ApiService.register(data)
                : await ApiService.completeRegisterGoogle(data, dataGoogle.access_token);

            if (res.status === 200) {
                if (!loggedByGoogle) {
                    const loginData = {
                        email: data.email,
                        password: data.password,
                    };
                    await handleLogIn(loginData, "register");
                } else {
                    afterCompleteRegisterGoogle();
                    const authMeData = await getConfigurationAuthMe(dataGoogle.access_token);
                    if (authMeData && !authMeData.data.roles.includes(4)) {
                        setOpenEstablishmentModal(true);
                    }
                    cleanUrl();
                }
                handleClose();
                handleShowAlert(
                    "success",
                    "Se registró con éxito. Debe esperar a que un administrador procese su solicitud!"
                );
                setSignUpFormData(DEFAULT_VALUE_FORM_MAP);
            } else {
                setErrorMessage("Ocurrió un error, intente nuevamente!");
                setShowError(true);
                setTimeout(() => {
                    setShowError(false);
                }, 3000);
            }
        } catch (error) {
            console.log(error);
            if (error.response.data.email) {
                setErrorMessage("El email ingresado ya se encuentra registrado");
            } else {
                setErrorMessage("Ocurrió un error, intente nuevamente!");
            }
            setShowError(true);
            setTimeout(() => {
                setShowError(false);
            }, 3000);
        }
        setOpenLoader(false);
    };

    return (
        <div>
            <Dialog open={open} onClose={handleClose}>
                {type === "login" ? (
                    <LogInForm
                        logInFormData={logInFormData}
                        setLogInFormData={setLogInFormData}
                        setOpenLoader={setOpenLoader}
                        handleLogIn={handleLogIn}
                        handleChangeForms={handleChangeForms}
                        handleClose={handleClose}
                        setShowError={setShowError}
                        showError={showError}
                        errorMessage={errorMessage}
                        setConfigurationGoogle={setConfigurationGoogle}
                    />
                ) : (
                    <SignUpForm
                        handleSignUp={handleSignUp}
                        signUpFormData={signUpFormData}
                        setSignUpFormData={setSignUpFormData}
                        handleChangeForms={handleChangeForms}
                        handleClose={handleClose}
                        setShowError={setShowError}
                        showError={showError}
                        errorMessage={errorMessage}
                        loggedByGoogle={loggedByGoogle}
                    />
                )}
            </Dialog>
            {openEstablishmentModal && (
                <Dialog
                    open={openEstablishmentModal}
                    // ESTE ONCLOSE NO VA
                    onClose={() => setOpenEstablishmentModal(false)}
                >
                    <EstablishmentSelectionModal
                        setOpen={setOpenEstablishmentModal}
                        checkPendingNotification={checkPendingNotification}
                        pendingNotificationIntervalId={pendingNotificationIntervalId}
                        setPendingNotificationIntervalId={setPendingNotificationIntervalId}
                        setRoleSelectedCallback={setRoleSelectedCallback}
                    ></EstablishmentSelectionModal>
                </Dialog>
            )}
            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: 999999999,
                }}
                open={openLoader}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            {showAlert && (
                <GenericAlert
                    severity={alertSeverity}
                    text={alertMsg}
                    style={{
                        position: "fixed",
                        zIndex: "9999",
                        top: "50%",
                        left: "30%",
                    }}
                />
            )}
        </div>
    );
}
